/**
 * Página de Produtos e Serviços do CRM
 * Permite gerenciar o catálogo de produtos e serviços
 * 
 * Versão: 3.0.0
 * - Layout premium padronizado
 * - KPI cards com estatísticas
 * - Header com gradiente e efeitos visuais
 * - Empty state premium
 */
import { useState, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useConfirmDialog } from '@/hooks/useConfirmDialog';
import { useTranslation } from 'react-i18next';
import { 
  Package, 
  Wrench, 
  Plus, 
  Search, 
  Edit, 
  Trash2, 
  MoreHorizontal, 
  Layers,
  RefreshCw,
  Loader2,
  CheckCircle,
  LucideIcon,
  Folder,
  FolderTree,
  Box,
  Laptop,
  Smartphone,
  Monitor,
  Headphones,
  Car,
  Home,
  Building,
  Coffee,
  ShoppingCart,
  Gift,
  Heart,
  Star,
  Zap,
  Target,
} from 'lucide-react';
import { StatusDot } from '@/components/tenant/SettingsInfoRow';

// Mapeamento de ícones disponíveis para categorias (espelha CrmProductCategories)
const CATEGORY_ICON_MAP: Record<string, LucideIcon> = {
  'folder': Folder,
  'folder-tree': FolderTree,
  'box': Box,
  'package': Package,
  'wrench': Wrench,
  'laptop': Laptop,
  'smartphone': Smartphone,
  'monitor': Monitor,
  'headphones': Headphones,
  'car': Car,
  'home': Home,
  'building': Building,
  'coffee': Coffee,
  'shopping-cart': ShoppingCart,
  'gift': Gift,
  'heart': Heart,
  'star': Star,
  'zap': Zap,
  'target': Target,
};

// Resolve o ícone da categoria pelo nome, com fallback para Folder
function getCategoryIconComponent(iconName?: string | null): LucideIcon {
  if (!iconName) return Folder;
  return CATEGORY_ICON_MAP[iconName] || Folder;
}
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { useAutoOpenFromQuery } from '@/hooks/useAutoOpenFromQuery';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { CategoryFilterSelect } from '@/components/crm/CategoryFilterSelect';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { CurrencyInput } from '@/components/ui/currency-input';
import { Checkbox } from '@/components/ui/checkbox';
import { useCrmProducts, type CreateProductInput, type CrmProduct } from '@/hooks/useCrmProducts';
import { useCrmCategories } from '@/hooks/useCrmCategories';
import { useCrmItemWorkspaces } from '@/hooks/useCrmItemWorkspaces';
import { WorkspaceMultiSelect } from '@/components/crm/WorkspaceMultiSelect';
import { usePermissions } from '@/hooks/usePermissions';
import {
  RECURRENCE_OPTIONS,
  TERM_TYPE_OPTIONS,
  TERM_UNIT_OPTIONS,
  calculateRecurringTotal,
  formatRecurrenceLabel,
  formatTermLabel,
  isRecurringService,
  type RecurrenceInterval,
  type TermType,
  type TermUnit,
} from '@/lib/crm/recurringServiceUtils';

// ============================================================
// INTERFACES E TIPOS
// ============================================================

/** Interface para estatísticas de produtos/serviços (KPIs) */
interface ProductStats {
  total_count: number;
  active_count: number;
  product_count: number;
  service_count: number;
  avg_price: number;
}

/** Props do componente MetricCard */
interface MetricCardProps {
  title: string;
  value: number;
  icon: LucideIcon;
  variant: 'primary' | 'success' | 'info' | 'warning';
  suffix?: string;
  prefix?: string;
}

/** Props do componente EmptyState */
interface EmptyStateProps {
  title: string;
  description: string;
  icon: LucideIcon;
  action?: {
    label: string;
    onClick: () => void;
  };
}

// ============================================================
// CONSTANTES
// ============================================================

// Tipos de unidade para PRODUTOS (físicos/mensuráveis)
const UNIT_TYPES_PRODUCT = [
  { value: 'un', label: 'Unidade' },
  { value: 'hora', label: 'Hora' },
  { value: 'dia', label: 'Dia' },
  { value: 'mes', label: 'Mês' },
  { value: 'kg', label: 'Quilograma' },
  { value: 'm', label: 'Metro' },
  { value: 'm2', label: 'Metro²' },
  { value: 'm3', label: 'Metro³' },
  { value: 'pacote', label: 'Pacote' },
  { value: 'caixa', label: 'Caixa' },
];

// Tipos de unidade para SERVIÇOS (modelo de cobrança)
const UNIT_TYPES_SERVICE = [
  { value: 'avulso', label: 'Avulso' },
  { value: 'recorrente', label: 'Recorrente' },
];

// Lista unificada usada para resolver labels em qualquer parte da UI
const UNIT_TYPES = [...UNIT_TYPES_PRODUCT, ...UNIT_TYPES_SERVICE];

// ============================================================
// COMPONENTES AUXILIARES
// ============================================================

/**
 * Estilos de variante para MetricCard - padrão unificado CRM
 */
const METRIC_CARD_STYLES = {
  primary: { cardBg: 'bg-purple-100 dark:bg-purple-900/30', iconColor: 'text-purple-700 dark:text-purple-300' },
  success: { cardBg: 'bg-lime-100 dark:bg-green-900/30', iconColor: 'text-green-700 dark:text-green-300' },
  info: { cardBg: 'bg-blue-100 dark:bg-blue-900/30', iconColor: 'text-blue-700 dark:text-blue-300' },
  warning: { cardBg: 'bg-cyan-100 dark:bg-cyan-900/30', iconColor: 'text-cyan-700 dark:text-cyan-300' },
};

/**
 * Card de métrica premium com shimmer effect - padrão unificado CRM
 */
function MetricCard({ title, value, icon: Icon, variant, suffix, prefix }: MetricCardProps) {
  const style = METRIC_CARD_STYLES[variant] || METRIC_CARD_STYLES.primary;
  return (
    <Card className={cn('border-0 shadow-none hover:shadow-md transition-shadow', style.cardBg)}>
      <CardContent className="p-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon className={cn('h-4 w-4', style.iconColor)} />
          <span className="text-xs font-medium text-muted-foreground">{title}</span>
        </div>
        <span className="text-xl font-bold text-foreground">
          {prefix}{typeof value === 'number' ? value.toLocaleString('pt-BR') : value}{suffix}
        </span>
      </CardContent>
    </Card>
  );
}

/**
 * Estado vazio premium com borda pontilhada e gradiente
 */
function EmptyState({ title, description, icon: Icon, action }: EmptyStateProps) {
  return (
    <div className="relative border-dashed border-2 border-border/50 rounded-xl bg-gradient-to-br from-muted/30 via-background to-muted/10 p-12 text-center">
      {/* Ícone circular com fundo */}
      <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-muted/50 mb-4">
        <Icon className="h-8 w-8 text-muted-foreground" />
      </div>
      
      <h3 className="text-lg font-semibold text-foreground mb-2">{title}</h3>
      <p className="text-muted-foreground text-sm max-w-sm mx-auto mb-6">{description}</p>
      
      {action && (
        <Button size="sm" onClick={action.onClick}>
          <Plus className="h-4 w-4 mr-2" />
          {action.label}
        </Button>
      )}
    </div>
  );
}

// ============================================================
// COMPONENTE PRINCIPAL
// ============================================================

export default function CrmProducts() {
  // Hook de confirmação premium para exclusão
  const { confirmDialog, ConfirmDialogComponent } = useConfirmDialog();
  const { t } = useTranslation();

  // === Controle de permissões granular ===
  const { canCreate, canEdit, canDelete } = usePermissions();
  const MENU_KEY = 'crm_products';
  const hasCreatePermission = canCreate(MENU_KEY);
  const hasEditPermission = canEdit(MENU_KEY);
  const hasDeletePermission = canDelete(MENU_KEY);
  const [activeTab, setActiveTab] = useState<'product' | 'service'>('product');
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  // v3.1.0: Filtros server-side adicionais para a aba Serviços
  const [selectedUnitType, setSelectedUnitType] = useState<string>('all');
  const [selectedRecurrence, setSelectedRecurrence] = useState<string>('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  // Auto-abre modal quando vindo do botão 'Novo' do header (Quick Create)
  useAutoOpenFromQuery('1', () => setDialogOpen(true));
  const [editingProduct, setEditingProduct] = useState<CrmProduct | null>(null);

  // Form state
  const [formData, setFormData] = useState<Partial<CreateProductInput>>({
    name: '',
    description: '',
    sku: '',
    category_id: null,
    product_type: 'product',
    unit_type: 'un',
    default_price: 0,
    cost_price: 0,
    is_open_price: false,
    recurring_price: null,
    recurrence_interval: null,
    term_type: null,
    term_quantity: null,
    term_unit: null,
  });

  // State para workspaces selecionados
  const [selectedWorkspaceIds, setSelectedWorkspaceIds] = useState<string[]>([]);

  // Hooks - buscar todos os produtos para estatísticas globais
  const { 
    products: allProducts, 
    isLoading: isLoadingAll,
    refetch: refetchAll 
  } = useCrmProducts({});
  
  // Produtos filtrados por tab/categoria/busca
  const { 
    products, 
    isLoading, 
    createProduct, 
    updateProduct, 
    deleteProduct,
    refetch 
  } = useCrmProducts({
    product_type: activeTab,
    category_id: selectedCategory === 'all' ? null : selectedCategory,
    search: search || undefined,
    // v3.1.0: filtros aplicados somente na aba Serviços (server-side)
    unit_type:
      activeTab === 'service' && selectedUnitType !== 'all'
        ? selectedUnitType
        : null,
    recurrence_interval:
      activeTab === 'service' &&
      selectedUnitType === 'recorrente' &&
      selectedRecurrence !== 'all'
        ? (selectedRecurrence as RecurrenceInterval)
        : null,
  });
  
  const { categories } = useCrmCategories();
  const { updateItemWorkspaces, fetchItemWorkspaces } = useCrmItemWorkspaces();

  // Calcular estatísticas globais
  const stats = useMemo<ProductStats>(() => {
    const activeCount = allProducts.filter(p => p.is_active).length;
    const productCount = allProducts.filter(p => p.product_type === 'product').length;
    const serviceCount = allProducts.filter(p => p.product_type === 'service').length;
    const avgPrice = allProducts.length > 0
      ? allProducts.reduce((acc, p) => acc + p.default_price, 0) / allProducts.length
      : 0;

    return {
      total_count: allProducts.length,
      active_count: activeCount,
      product_count: productCount,
      service_count: serviceCount,
      avg_price: Math.round(avgPrice * 100) / 100,
    };
  }, [allProducts]);

  // Formatar preço
  const formatPrice = (price: number, currency = 'BRL') => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency,
    }).format(price);
  };

  // Atualizar dados
  const handleRefresh = () => {
    refetch();
    refetchAll();
  };

  // Abrir dialog para criar
  const handleCreate = () => {
    setEditingProduct(null);
    // Default de unidade depende do tipo (produto -> 'un', serviço -> 'avulso')
    const defaultUnit = activeTab === 'service' ? 'avulso' : 'un';
    setFormData({
      name: '',
      description: '',
      sku: '',
      category_id: null,
      product_type: activeTab,
      unit_type: defaultUnit,
      default_price: 0,
      cost_price: 0,
      is_open_price: false,
      recurring_price: null,
      recurrence_interval: null,
      term_type: null,
      term_quantity: null,
      term_unit: null,
    });
    setSelectedWorkspaceIds([]);
    setDialogOpen(true);
  };

  // Abrir dialog para editar
  const handleEdit = async (product: CrmProduct) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      description: product.description || '',
      sku: product.sku || '',
      category_id: product.category_id,
      product_type: product.product_type,
      unit_type: product.unit_type,
      default_price: product.default_price,
      cost_price: product.cost_price || 0,
      is_open_price: product.is_open_price ?? false,
      recurring_price: product.recurring_price,
      recurrence_interval: product.recurrence_interval,
      term_type: product.term_type,
      term_quantity: product.term_quantity,
      term_unit: product.term_unit,
    });
    // Carregar workspaces vinculados
    try {
      const wsIds = await fetchItemWorkspaces('product', product.id);
      setSelectedWorkspaceIds(wsIds);
    } catch {
      setSelectedWorkspaceIds([]);
    }
    setDialogOpen(true);
  };

  // Salvar produto
  const handleSave = async () => {
    if (!formData.name) return;
    if (selectedWorkspaceIds.length === 0) return;

    // Detecta se é serviço recorrente — se não for, força NULL nos campos
    const recurring = isRecurringService(formData.product_type, formData.unit_type);
    const isIndet = recurring && formData.term_type === 'indeterminado';

    // Normalizar SKU + campos de recorrência (defensivo; trigger no banco também valida)
    const normalizedData = {
      ...formData,
      sku: formData.sku?.trim() || null,
      recurring_price: recurring ? formData.recurring_price ?? null : null,
      recurrence_interval: recurring ? formData.recurrence_interval ?? null : null,
      term_type: recurring ? formData.term_type ?? null : null,
      term_quantity: recurring && !isIndet ? formData.term_quantity ?? null : null,
      term_unit: recurring && !isIndet ? formData.term_unit ?? null : null,
    };

    if (editingProduct) {
      await updateProduct.mutateAsync({
        id: editingProduct.id,
        ...normalizedData,
      });
      // Atualizar workspaces
      await updateItemWorkspaces.mutateAsync({
        itemType: 'product',
        itemId: editingProduct.id,
        workspaceIds: selectedWorkspaceIds,
      });
    } else {
      const newProduct = await createProduct.mutateAsync(normalizedData as CreateProductInput);
      // Vincular workspaces
      await updateItemWorkspaces.mutateAsync({
        itemType: 'product',
        itemId: newProduct.id,
        workspaceIds: selectedWorkspaceIds,
      });
    }
    setDialogOpen(false);
    handleRefresh();
  };

  /**
   * Handler de exclusão com validação de vínculos
   * Verifica cobranças vinculadas antes de permitir exclusão
   */
  const handleDelete = async (product: CrmProduct) => {
    // Verificar cobranças vinculadas ao produto
    const { count } = await supabase
      .from('financeiro_cobranca_itens')
      .select('*', { count: 'exact', head: true })
      .eq('product_id', product.id)
      .is('deleted_at', null);

    if (count && count > 0) {
      // Possui vínculos — informar e bloquear exclusão
      await confirmDialog({
        variant: 'info',
        title: 'Não é possível remover',
        description: `O ${product.product_type === 'product' ? 'produto' : 'serviço'} "${product.name}" possui ${count} cobrança(s) vinculada(s). Desative-o em vez de excluir.`,
        confirmText: 'Entendi',
      });
    } else {
      // Sem vínculos — confirmar exclusão
      const confirmed = await confirmDialog({
        variant: 'danger',
        title: `Remover ${product.product_type === 'product' ? 'produto' : 'serviço'}?`,
        description: `Tem certeza que deseja excluir "${product.name}"? Esta ação não pode ser desfeita.`,
        confirmText: 'Remover',
      });
      if (confirmed) {
        await deleteProduct.mutateAsync(product.id);
      }
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* ============================================================ */}
      {/* HEADER COMPACTO - Padrão unificado CRM */}
      {/* ============================================================ */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 border-b">
        <div>
          <h1 className="text-xl font-semibold">Produtos e Serviços</h1>
        </div>
        {hasCreatePermission && (
          <Button 
            size="xs" 
            onClick={handleCreate}
            className="shrink-0"
          >
            <Plus className="mr-1.5" />
            Novo {activeTab === 'product' ? 'Produto' : 'Serviço'}
          </Button>
        )}
      </div>

      {/* ============================================================ */}
      {/* KPI CARDS - Padrão unificado CRM */}
      {/* ============================================================ */}
      {!isLoadingAll && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 px-4 py-3 border-b">
          <MetricCard
            title="Total de Itens"
            value={stats.total_count}
            icon={Layers}
            variant="primary"
          />
          <MetricCard
            title="Ativos"
            value={stats.active_count}
            icon={CheckCircle}
            variant="success"
          />
          <MetricCard
            title="Produtos"
            value={stats.product_count}
            icon={Package}
            variant="info"
          />
          <MetricCard
            title="Serviços"
            value={stats.service_count}
            icon={Wrench}
            variant="warning"
          />
        </div>
      )}

      {/* ============================================================ */}
      {/* BARRA DE FILTROS - Padrão unificado CRM */}
      {/* ============================================================ */}
      <div className="flex flex-wrap items-center gap-2 p-3 border-b bg-muted/30">
        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v as 'product' | 'service'); setSelectedUnitType('all'); setSelectedRecurrence('all'); }} className="mr-auto">
          <TabsList className="h-8 bg-background/50">
            <TabsTrigger value="product" className="h-7 text-xs px-3 flex items-center gap-1.5">
              <Package className="h-3.5 w-3.5" />
              Produtos
            </TabsTrigger>
            <TabsTrigger value="service" className="h-7 text-xs px-3 flex items-center gap-1.5">
              <Wrench className="h-3.5 w-3.5" />
              Serviços
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Categoria */}
        <CategoryFilterSelect
          value={selectedCategory === 'all' ? null : selectedCategory}
          onChange={(id) => setSelectedCategory(id ?? 'all')}
          placeholder="Todas categorias"
          className="w-[160px]"
          size="sm"
        />

        {/* v3.1.0: Filtros de Unidade e Periodicidade — apenas na aba Serviços */}
        {activeTab === 'service' && (
          <>
            <Select
              value={selectedUnitType}
              onValueChange={(v) => {
                setSelectedUnitType(v);
                // Reseta periodicidade quando muda para algo que não é recorrente
                if (v !== 'recorrente') setSelectedRecurrence('all');
              }}
            >
              <SelectTrigger className="w-[140px] h-8 text-sm">
                <SelectValue placeholder="Unidade" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas unidades</SelectItem>
                {UNIT_TYPES_SERVICE.map((u) => (
                  <SelectItem key={u.value} value={u.value}>
                    {u.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              value={selectedRecurrence}
              onValueChange={setSelectedRecurrence}
              disabled={selectedUnitType !== 'recorrente'}
            >
              <SelectTrigger className="w-[150px] h-8 text-sm">
                <SelectValue placeholder="Periodicidade" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas periodicidades</SelectItem>
                {RECURRENCE_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </>
        )}


        {/* Busca */}
        <div className="relative flex-1 min-w-[180px] max-w-sm">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            placeholder="Buscar por nome, SKU..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8 h-8 text-sm"
          />
        </div>

        {/* Indicador de carregamento */}
        {isLoading && (
          <span className="text-xs text-muted-foreground animate-pulse">
            Atualizando...
          </span>
        )}
      </div>

      {/* ============================================================ */}
      {/* CONTEÚDO - TABELA DE PRODUTOS/SERVIÇOS */}
      {/* ============================================================ */}
      <div className="flex-1 overflow-auto">
          <Card className="border-border/50 overflow-hidden bg-gradient-to-br from-background to-muted/10">
            {/* Header da tabela */}
            <div className="px-6 py-4 border-b border-border/50 bg-muted/20">
              <div className="flex items-center gap-3">
                <h2 className="font-semibold text-foreground">
                  {activeTab === 'product' ? 'Lista de Produtos' : 'Lista de Serviços'}
                </h2>
                <Badge variant="secondary" className="font-normal">
                  {products.length} encontrado{products.length !== 1 ? 's' : ''}
                </Badge>
                {isLoading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
              </div>
            </div>

            {/* Conteúdo */}
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : products.length === 0 ? (
              <div className="p-6">
                <EmptyState
                  title={search 
                    ? `Nenhum ${activeTab === 'product' ? 'produto' : 'serviço'} encontrado`
                    : `Nenhum ${activeTab === 'product' ? 'produto' : 'serviço'} cadastrado`
                  }
                  description={search 
                    ? 'Tente buscar por outro termo ou limpe os filtros.'
                    : `Comece cadastrando o primeiro ${activeTab === 'product' ? 'produto' : 'serviço'} do seu catálogo.`
                  }
                  icon={activeTab === 'product' ? Package : Wrench}
                  action={!search && hasCreatePermission ? {
                    label: `Criar ${activeTab === 'product' ? 'Produto' : 'Serviço'}`,
                    onClick: handleCreate,
                  } : undefined}
                />
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/30 hover:bg-muted/40">
                      <TableHead className="font-semibold">Nome</TableHead>
                      <TableHead className="font-semibold">SKU</TableHead>
                      <TableHead className="font-semibold">Categoria</TableHead>
                      <TableHead className="font-semibold">Unidade</TableHead>
                      {/* Colunas específicas de serviços recorrentes (Mensalidade + Prazo) */}
                      {activeTab === 'service' && (
                        <>
                          <TableHead className="font-semibold text-right">Mensalidade</TableHead>
                          <TableHead className="font-semibold">Prazo</TableHead>
                        </>
                      )}
                      <TableHead className="font-semibold text-right">Preço</TableHead>
                      <TableHead className="font-semibold">Status</TableHead>
                      <TableHead className="w-[50px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {products.map((product) => (
                      <TableRow 
                        key={product.id}
                        className="group hover:bg-muted/20 transition-colors"
                      >
                        <TableCell>
                          <div>
                            <p className="font-medium">{product.name}</p>
                            {product.description && (
                              <p className="text-sm text-muted-foreground line-clamp-1">
                                {product.description}
                              </p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          {product.sku ? (
                            <code className="text-sm bg-muted px-1.5 py-0.5 rounded">
                              {product.sku}
                            </code>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {product.category ? (
                            (() => {
                              // Renderiza ícone (com cor de fundo suave) + nome em preto
                              const CategoryIcon = getCategoryIconComponent(product.category.icon);
                              const color = product.category.color || '#6366f1';
                              return (
                                <div className="flex items-center gap-2 min-w-0">
                                  <div
                                    className="h-7 w-7 rounded-md flex items-center justify-center shrink-0"
                                    style={{ backgroundColor: `${color}20` }}
                                  >
                                    <CategoryIcon className="h-4 w-4" style={{ color }} />
                                  </div>
                                  <span className="font-medium text-foreground truncate">
                                    {product.category.name}
                                  </span>
                                </div>
                              );
                            })()
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {isRecurringService(product.product_type, product.unit_type) ? (
                            <Badge variant="secondary" className="gap-1 font-normal">
                              <RefreshCw className="h-3 w-3" />
                              Recorrente · {formatRecurrenceLabel(product.recurrence_interval)}
                            </Badge>
                          ) : (
                            UNIT_TYPES.find((u) => u.value === product.unit_type)?.label || product.unit_type
                          )}
                        </TableCell>
                        {/* Células específicas de serviços: Mensalidade + Prazo */}
                        {activeTab === 'service' && (
                          <>
                            <TableCell className="text-right font-medium">
                              {isRecurringService(product.product_type, product.unit_type) && product.recurring_price
                                ? formatPrice(product.recurring_price, product.currency)
                                : <span className="text-muted-foreground">—</span>}
                            </TableCell>
                            <TableCell>
                              {isRecurringService(product.product_type, product.unit_type) && product.term_type ? (
                                <span className="text-sm">
                                  {formatTermLabel(product.term_type, product.term_quantity, product.term_unit) || '—'}
                                </span>
                              ) : (
                                <span className="text-muted-foreground">—</span>
                              )}
                            </TableCell>
                          </>
                        )}
                        <TableCell className="text-right font-medium">
                          {/* Preço aberto: exibe badge no lugar do valor fixo */}
                          {product.is_open_price ? (
                            <Badge variant="outline" className="text-[10px] uppercase tracking-wider">
                              Sob demanda
                            </Badge>
                          ) : (
                            formatPrice(product.default_price, product.currency)
                          )}
                        </TableCell>
                        <TableCell>
                          {/* Status — padrão Departamentos: Badge outline + StatusDot */}
                          <Badge
                            variant="outline"
                            className="h-5 gap-1.5 px-1.5 text-[10px] font-medium uppercase tracking-wider"
                          >
                            <StatusDot tone={product.is_active ? 'emerald' : 'muted'} />
                            {product.is_active ? 'Ativo' : 'Inativo'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8">
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              {hasEditPermission && (
                                <DropdownMenuItem onClick={() => handleEdit(product)}>
                                  <Edit className="h-4 w-4 mr-2" />
                                  Editar
                                </DropdownMenuItem>
                              )}
                              {hasDeletePermission && (
                                <DropdownMenuItem
                                  onClick={() => handleDelete(product)}
                                  className="text-destructive"
                                >
                                  <Trash2 className="h-4 w-4 mr-2" />
                                  Excluir
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </Card>
      </div>

      {/* ============================================================ */}
      {/* DIALOG DE CRIAÇÃO/EDIÇÃO */}
      {/* ============================================================ */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingProduct ? 'Editar' : 'Novo'} {activeTab === 'product' ? 'Produto' : 'Serviço'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid gap-4">
              <div className="space-y-2">
                <Label>Nome *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Nome do produto/serviço"
                />
              </div>

              <div className="space-y-2">
                <Label>Descrição</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Descrição opcional"
                  rows={3}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>SKU</Label>
                  <Input
                    value={formData.sku}
                    onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
                    placeholder="Código único"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Categoria</Label>
                  <CategoryFilterSelect
                    value={formData.category_id}
                    onChange={(id) => setFormData({ ...formData, category_id: id })}
                    placeholder="Sem categoria"
                    size="default"
                    className="w-full"
                    leafOnly
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Unidade</Label>
                  <Select
                    value={formData.unit_type}
                    onValueChange={(v) => {
                      // Ao virar "recorrente", inicializa term_type='determinado' para o usuário ver os campos
                      const switchingToRec = v === 'recorrente' && formData.unit_type !== 'recorrente';
                      setFormData({
                        ...formData,
                        unit_type: v,
                        ...(switchingToRec
                          ? { term_type: 'determinado' as TermType }
                          : v !== 'recorrente'
                            ? {
                                recurring_price: null,
                                recurrence_interval: null,
                                term_type: null,
                                term_quantity: null,
                                term_unit: null,
                              }
                            : {}),
                      });
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {(formData.product_type === 'service' ? UNIT_TYPES_SERVICE : UNIT_TYPES_PRODUCT).map((unit) => (
                        <SelectItem key={unit.value} value={unit.value}>
                          {unit.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>
                    {formData.is_open_price ? 'Preço Padrão (sugerido)' : 'Preço Padrão *'}
                  </Label>
                  <CurrencyInput
                    value={formData.default_price}
                    onChange={(value) => setFormData({ ...formData, default_price: value })}
                    placeholder="R$ 0,00"
                    disabled={formData.is_open_price}
                  />
                </div>
              </div>

              {/* Preço sob demanda — item sem valor fixo (ex.: honorários jurídicos) */}
              <div className="flex items-start gap-2 rounded-md border p-3">
                <Checkbox
                  id="is_open_price"
                  checked={!!formData.is_open_price}
                  onCheckedChange={(checked) =>
                    setFormData({
                      ...formData,
                      is_open_price: checked === true,
                      // Ao ligar, zera o preço fixo para evitar valor residual
                      ...(checked === true ? { default_price: 0 } : {}),
                    })
                  }
                />
                <div className="space-y-0.5">
                  <Label htmlFor="is_open_price" className="cursor-pointer">
                    Preço sob demanda (valor aberto)
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    O item não possui valor fixo. O preço será informado manualmente na criação
                    da cobrança, proposta ou negócio.
                  </p>
                </div>
              </div>


              <div className="space-y-2">
                <Label>Preço de Custo</Label>
                <CurrencyInput
                  value={formData.cost_price}
                  onChange={(value) => setFormData({ ...formData, cost_price: value })}
                  placeholder="R$ 0,00"
                />
              </div>

              {/* ============================================================ */}
              {/* BLOCO DE SERVIÇO RECORRENTE (condicional)                    */}
              {/* ============================================================ */}
              {isRecurringService(formData.product_type, formData.unit_type) && (
                <div className="space-y-4 rounded-lg border border-primary/20 bg-primary/5 p-4">
                  <div className="flex items-center gap-2 text-sm font-semibold text-primary">
                    <RefreshCw className="h-4 w-4" />
                    Configuração de Recorrência
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Mensalidade *</Label>
                      <CurrencyInput
                        value={formData.recurring_price ?? 0}
                        onChange={(value) => setFormData({ ...formData, recurring_price: value })}
                        placeholder="R$ 0,00"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>Periodicidade *</Label>
                      <Select
                        value={formData.recurrence_interval ?? ''}
                        onValueChange={(v) =>
                          setFormData({ ...formData, recurrence_interval: v as RecurrenceInterval })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione" />
                        </SelectTrigger>
                        <SelectContent>
                          {RECURRENCE_OPTIONS.map((opt) => (
                            <SelectItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Prazo do contrato *</Label>
                    <Select
                      value={formData.term_type ?? ''}
                      onValueChange={(v) =>
                        setFormData({
                          ...formData,
                          term_type: v as TermType,
                          // Indeterminado limpa qty/unit; determinado mantém atuais
                          ...(v === 'indeterminado'
                            ? { term_quantity: null, term_unit: null }
                            : {}),
                        })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione" />
                      </SelectTrigger>
                      <SelectContent>
                        {TERM_TYPE_OPTIONS.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Quantidade + unidade só aparecem para prazo determinado */}
                  {formData.term_type === 'determinado' && (
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Quantidade *</Label>
                        <Input
                          type="number"
                          min={1}
                          value={formData.term_quantity ?? ''}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              term_quantity: e.target.value ? parseInt(e.target.value, 10) : null,
                            })
                          }
                          placeholder="Ex.: 12"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Unidade do prazo *</Label>
                        <Select
                          value={formData.term_unit ?? ''}
                          onValueChange={(v) =>
                            setFormData({ ...formData, term_unit: v as TermUnit })
                          }
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Selecione" />
                          </SelectTrigger>
                          <SelectContent>
                            {TERM_UNIT_OPTIONS.map((opt) => (
                              <SelectItem key={opt.value} value={opt.value}>
                                {opt.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  )}

                  {/* Prévia do total estimado do contrato */}
                  {(() => {
                    if (formData.term_type === 'indeterminado') {
                      return (
                        <p className="text-xs text-muted-foreground italic">
                          Contrato sem prazo definido — o valor total não é calculado.
                        </p>
                      );
                    }
                    const total = calculateRecurringTotal({
                      recurring_price: formData.recurring_price,
                      recurrence_interval: formData.recurrence_interval,
                      term_type: formData.term_type,
                      term_quantity: formData.term_quantity,
                      term_unit: formData.term_unit,
                    });
                    if (total === null) return null;
                    return (
                      <div className="flex items-center justify-between rounded-md bg-background/60 px-3 py-2 text-sm">
                        <span className="text-muted-foreground">Total estimado do contrato</span>
                        <span className="font-semibold text-foreground">
                          {formatPrice(total, formData.currency || 'BRL')}
                        </span>
                      </div>
                    );
                  })()}
                </div>
              )}

              {/* Workspaces */}
              <div className="space-y-2">
                <Label>Workspaces *</Label>
                <WorkspaceMultiSelect
                  value={selectedWorkspaceIds}
                  onChange={setSelectedWorkspaceIds}
                  placeholder="Selecione pelo menos um workspace..."
                  required
                />
                <p className="text-xs text-muted-foreground">
                  Selecione os workspaces onde este {activeTab === 'product' ? 'produto' : 'serviço'} estará disponível
                </p>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleSave}
              disabled={!formData.name || selectedWorkspaceIds.length === 0 || createProduct.isPending || updateProduct.isPending}
            >
              {editingProduct ? 'Salvar' : 'Criar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ConfirmDialog premium (renderizado pelo hook useConfirmDialog) */}
      {ConfirmDialogComponent}
    </div>
  );
}
